import React, { useState, useEffect } from 'react';
import { Wallet, Clock, AlertCircle, CheckCircle, Shield, Activity, Key, Lock, Loader } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEVM } from '../contexts/EVMProvider';
import ChainSelector from './ChainSelector';
import EVMWalletButton from './EVMWalletButton';
import EVMClaimInterface from './EVMClaimInterface';
import '../App.css';
import '../index.css';

const ConnectPage = () => {
  const { getWalletInfo, EVM_CHAINS, selectedEVMChain } = useEVM();
  const walletInfo = getWalletInfo();
  
  // Determine if wallet is connected
  const isWalletConnected = walletInfo.isConnected;
  
  const [isChainLoading, setIsChainLoading] = useState(false);

  // Add loading state when chain changes
  useEffect(() => {
    setIsChainLoading(true);
    const timer = setTimeout(() => setIsChainLoading(false), 500);
    return () => clearTimeout(timer);
  }, [selectedEVMChain]);

  return (
    <div className="min-h-screen text-white relative">
      {/* Chain Loading Overlay */}
      {isChainLoading && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="glass-dark rounded-xl p-6 flex flex-col items-center space-y-4">
            <Loader className="w-8 h-8 animate-spin text-primary-400" />
            <p className="text-white font-medium">Switching to {EVM_CHAINS[selectedEVMChain]?.name || 'EVM'} Network...</p>
          </div>
        </div>
      )}
      
      {/* Animated Background Particles */}
      <div className="particles">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="particle" />
        ))}
      </div>

      {/* Navigation */}
      <nav className="relative z-[9999] px-4 sm:px-6 py-4 sm:py-5 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-primary rounded-lg flex items-center justify-center shadow-glow">
            <span className="text-white font-bold text-sm sm:text-lg">E</span>
          </div>
          <span className="font-bold text-lg sm:text-xl text-gradient">EVSectionResolver</span>
        </Link>
        
        <div className="flex items-center space-x-2 sm:space-x-4">
          <ChainSelector />
          <EVMWalletButton />
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-1 max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-16 sm:pb-24">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gradient">
            Resolve Your EVM Token Issues
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto px-4">
            Connect your wallet to access our advanced troubleshooting tools and get your crypto back on track.
          </p>
          
          {/* Chain Status */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-primary-400 shadow-glow"></div>
              <span className="text-white/80 text-sm sm:text-base">
                {EVM_CHAINS[selectedEVMChain]?.name || 'EVM Chain'}
              </span>
            </div>
            {isWalletConnected && (
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-primary-400" />
                <span className="text-sm text-primary-400">
                  Wallet Connected
                </span>
              </div>
            )}
          </div>
          
          {/* Mobile Chain Indicator */}
          <div className="mt-4 sm:hidden">
            <div className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg glass border border-primary-500/30">
              <div className="w-2 h-2 rounded-full bg-primary-400 shadow-glow"></div>
              <span className="text-xs font-medium text-primary-400">
                EVM
              </span>
            </div>
          </div>
        </div>

        {/* Troubleshooting Card */}
        <div className="max-w-md mx-auto rounded-2xl glass shadow-glow-lg border border-white/20 overflow-hidden">
          {/* Card Header */}
          <div className="px-4 sm:px-6 py-4 border-b glass border-white/20">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-semibold">
                EVM Token Resolution Portal
              </h2>
              <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gradient-primary shadow-glow">
                <span className="text-white text-xs font-bold">
                  E
                </span>
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="px-4 sm:px-6 py-6">
              <EVMClaimInterface />
          </div>
        </div>

        {/* How Our Troubleshooting Works */}
        <div className="mt-16 sm:mt-24">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 sm:mb-12">How Our Troubleshooting Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/10 transition-all">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-700/50">
                <Wallet size={20} className="text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Connect Wallet</h3>
              <p className="text-gray-400">Link your EVM wallet to access our diagnostic tools.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/10 transition-all">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-700/50">
                <Activity size={20} className="text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Diagnose Issues</h3>
              <p className="text-gray-400">Our AI-powered system identifies and analyzes EVM wallet problems.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/10 transition-all">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-700/50">
                <Shield size={20} className="text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Secure Recovery</h3>
              <p className="text-gray-400">Get your EVM wallet back on track with military-grade security.</p>
            </div>
          </div>
        </div>

        {/* Troubleshooting Services */}
        <div className="mt-16 sm:mt-24">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 sm:mb-12">Our Troubleshooting Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Key size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Private Key Recovery</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Recover lost EVM private keys and seed phrases.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Lock size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Smart Contract Issues</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Fix compromised EVM wallets and contract interactions.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Clock size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Gas Issues</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Resolve EVM gas estimation and transaction problems.</p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-700/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <AlertCircle size={18} className="sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Network Issues</h3>
                  <p className="text-gray-400 text-sm sm:text-base">Fix failed EVM transactions and network connectivity.</p>
                </div>
          </div>
        </div>

        {/* Why Choose SolFix */}
        <div className="mt-16 sm:mt-24">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 sm:mb-12">Why Choose SolFix for Troubleshooting</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 bg-blue-700/50">
                <CheckCircle size={18} className="sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">98% Success Rate</h3>
              <p className="text-gray-400 text-sm sm:text-base">Proven track record of successful EVM wallet recoveries.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 bg-blue-700/50">
                <Shield size={18} className="sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">Military-Grade Security</h3>
              <p className="text-gray-400 text-sm sm:text-base">Bank-level encryption protects your EVM wallet data.</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center hover:bg-white/10 transition-all">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 bg-blue-700/50">
                <Clock size={18} className="sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">24/7 Expert Support</h3>
              <p className="text-gray-400 text-sm sm:text-base">Round-the-clock assistance from EVM blockchain specialists.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-green-800/50 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <span className="font-bold">SolFix</span>
            </div>
            <div className="text-xs sm:text-sm text-white/60 text-center md:text-left">
              © 2024 EVSectionResolver. Professional EVM Token Resolution Services.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConnectPage; 